/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document is loose-typed by design (see HeartReportEditCards). */

import React, { ReactElement, useState } from "react"
import {
  SOURCE_OUTCOME_LABELS,
  THIRD_PARTY_FEEDS,
  contextSummaryForFeature,
  feedAgreement,
  feedRole,
  feedState,
  getCardRule,
  sourceIdForFeed,
  viewerDecisionOf,
} from "./core"

/**
 * A card's Evidence details panel in clinical review — React rebuild of the
 * prototype's feed inspector (cardiac_report_v0_5_8.html at cardiac-panel-v4
 * git 6c66459): a compact bar naming the card's feeds, which opens to the
 * card's rule and one row per feed with its role (Medaica result / provider
 * evidence), its state, and what it found. A provider row (ACT, BeatAware)
 * lists the recordings behind it, each with the decision recorded on that
 * recording in the PCG viewer's plugin rack — read-only here: the viewer is
 * where the decision is taken.
 *
 * Information for the person preparing the report only: nothing here
 * changes whether the card is in the report, and none of it reaches the
 * patient or provider documents.
 *
 * Deliberate deviations from the prototype:
 * - The vendor rows are the stored ACT / BeatAware results of the accepted
 *   recordings, each with its plugin review; upstream shows one finding per
 *   provider and derives a single badge from the source's stored disposition.
 * - No "matched / differed" concordance badge on the rows after approval
 *   (upstream guesses it from keywords in the finding text); the approved
 *   summary keeps upstream's counts.
 * - No "+ Attach feed or context" slot (it only announced a pending API).
 * - Open / closed is view state, not stored in the document.
 */

const isVendorSource = (source: any): boolean => THIRD_PARTY_FEEDS.includes(sourceIdForFeed(source))

// The state badge: a disposition recorded by an earlier report panel, else
// whether the feed still counts as evidence for the card.
const feedStateLabel = (feature: any, source: any): string => {
  const stored = SOURCE_OUTCOME_LABELS[source.evidenceDisposition as keyof typeof SOURCE_OUTCOME_LABELS]
  if (stored) {
    return stored
  }
  return feedState(feature, source) === "excluded_from_evidence" ? "Not used in this report" : "Available evidence"
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`

// The collapsed bar: the feeds and providers on the card before approval,
// how each feed compared with the approved card afterwards.
const evidenceSummary = (feature: any, questionnaire: unknown): string => {
  const sources: any[] = feature.sources || []
  if (!sources.length) {
    return "Evidence details · no feeds"
  }
  const context = contextSummaryForFeature(feature, questionnaire)
  const parts = [plural(sources.length, "feed")]
  if (!feature.approved) {
    const providers = sources.filter((source) => sourceIdForFeed(source) !== "pcg_viewer").length
    if (providers) {
      parts.push(plural(providers, "provider"))
    }
  } else {
    const agreements = sources.map((source) => feedAgreement(feature, source))
    const count = (agreement: string) => agreements.filter((item) => item === agreement).length
    const aligned = count("concordant")
    const differed = count("discordant")
    const excluded = count("excluded")
    if (aligned) {
      parts.push(`${aligned} aligned`)
    }
    if (differed) {
      parts.push(`${differed} differed`)
    }
    if (excluded) {
      parts.push(`${excluded} excluded`)
    }
  }
  if (context) {
    parts.push(context)
  }
  return `Evidence details · ${parts.join(" · ")}`
}

// Green for support, red against, grey when the viewer recorded no decision.
const DECISION_TONES: { [outcome: string]: string } = { supports: "supports", does_not_support: "against" }

const ViewerDecision = ({ recording }: { recording: any }): ReactElement => {
  const decision = viewerDecisionOf(recording)
  const tone = (decision.outcome && DECISION_TONES[decision.outcome]) || "unreviewed"
  return (
    <span className={`phr-evidence-decision phr-evidence-decision--${tone}`}>Viewer decision: {decision.label}</span>
  )
}

const HeartReportEvidencePanel = ({
  feature,
  questionnaire,
}: {
  feature: any
  questionnaire?: unknown
}): ReactElement => {
  const [open, setOpen] = useState(false)
  const name = String(feature.name)
  const sources: any[] = feature.sources || []

  return (
    <div className="phr-evidence" data-testid={`evidence-${name}`}>
      <button
        type="button"
        className="phr-evidence-toggle"
        aria-expanded={open}
        aria-label={`${name} evidence`}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{evidenceSummary(feature, questionnaire)}</span>
        <span aria-hidden="true">{open ? "▾" : "›"}</span>
      </button>
      {open ? (
        <div className="phr-evidence-body">
          <div className="phr-evidence-rule">{getCardRule(feature).summary}</div>
          {sources.map((source, sourceIndex) => {
            const vendor = isVendorSource(source)
            const excluded = source.reviewStatus === "rejected"
            return (
              <div
                key={`${sourceIdForFeed(source)}-${sourceIndex}`}
                className={`phr-evidence-row${excluded ? " phr-evidence-row--excluded" : ""}`}
                data-testid={`evidence-row-${name}-${sourceIdForFeed(source)}`}
              >
                <div className="phr-evidence-row-head">
                  <div className="phr-evidence-badges">
                    <span className="phr-evidence-source">Source: {source.name || source.sourceLabel}</span>
                    <span className="phr-evidence-badge">{feedRole(feature, source)}</span>
                    <span className="phr-evidence-badge">{feedStateLabel(feature, source)}</span>
                  </div>
                </div>
                {vendor ? (
                  <ul className="phr-evidence-findings">
                    {(source.recordings || []).map((recording: any) => (
                      <li key={recording.recordingId}>
                        <span className="phr-evidence-position">{recording.position || "Recording"}</span>{" "}
                        {recording.value} <ViewerDecision recording={recording} />
                        {recording.viewerReview?.note ? ` — “${recording.viewerReview.note}”` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="phr-evidence-finding">{source.finding}</div>
                )}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default HeartReportEvidencePanel
